import { fromWallClock, wallClockIn } from './recurrence.js';
/** Ha csak nap van megadva, ora nelkul: reggel 9. */
const DEFAULT_HOUR = 9;
/** A napszak alapideje, ha ora nem tartozik hozza ("délután" = 14:00). */
const DAYPART = {
    reggel: { hour: 8, pm: false },
    délelőtt: { hour: 10, pm: false },
    délben: { hour: 12, pm: false },
    délután: { hour: 14, pm: true },
    este: { hour: 19, pm: true },
    éjjel: { hour: 22, pm: true },
};
/** Hetnap a szo elejerol - 0 = vasarnap, mint a `Date`-nel. */
const WEEKDAYS = [
    [/^hétfő/, 1],
    [/^kedd/, 2],
    [/^szerd/, 3],
    [/^csütörtök/, 4],
    [/^péntek/, 5],
    [/^szombat/, 6],
    [/^vasárnap/, 0],
];
const MONTHS = [
    [/^jan/, 1],
    [/^feb/, 2],
    [/^márc/, 3],
    [/^ápr/, 4],
    [/^máj/, 5],
    [/^jún/, 6],
    [/^júl/, 7],
    [/^aug/, 8],
    [/^szep/, 9],
    [/^okt/, 10],
    [/^nov/, 11],
    [/^dec/, 12],
];
const NUMBER_WORDS = {
    egy: 1,
    két: 2,
    kettő: 2,
    három: 3,
    négy: 4,
    öt: 5,
    hat: 6,
    hét: 7,
    nyolc: 8,
    kilenc: 9,
    tíz: 10,
    tizenöt: 15,
    húsz: 20,
    harminc: 30,
    negyven: 40,
    negyvenöt: 45,
};
// A `\b` nem ismeri az ekezetes betuket - sajat szohatar kell.
const B = '(?<![\\p{L}\\p{N}])';
const E = '(?![\\p{L}\\p{N}])';
const NUM = `(\\d{1,3}|${Object.keys(NUMBER_WORDS).join('|')}|fél|másfél)`;
const RELATIVE = new RegExp(`${B}${NUM}\\s*(perc|óra|nap|hét)\\s*(?:múlva|mulva)${E}`, 'iu');
const DAY_WORD = new RegExp(`${B}(holnapután|holnap|ma)${E}`, 'iu');
const WEEKDAY = new RegExp(`${B}(jövő\\s+)?(hétfőn|kedden|szerdán|csütörtökön|pénteken|szombaton|vasárnap)${E}`, 'iu');
const ISO_DATE = new RegExp(`${B}(\\d{4})[-.]\\s?(\\d{1,2})[-.]\\s?(\\d{1,2})\\.?(?:-?[aáeé]n)?${E}`, 'u');
const MONTH_DAY = new RegExp(`${B}(jan|febr?|márc|ápr|máj|jún|júl|aug|szept?|okt|nov|dec)[\\p{L}]*\\.?\\s*(\\d{1,2})\\.?(?:-?[aáeé]n|-?i)?${E}`, 'iu');
const NUMERIC_DATE = new RegExp(`${B}(\\d{1,2})\\.\\s?(\\d{1,2})\\.(?:-?[aáeé]n)?(?![\\p{L}\\p{N}.])`, 'u');
const DAYPART_RE = new RegExp(`${B}(reggel|délelőtt|délben|délután|este|éjjel)${E}`, 'iu');
// "fél 10", "negyed 10", "háromnegyed 10" - a magyar ora a KOVETKEZO orahoz merve.
const FRACTION_CLOCK = new RegExp(`${B}(háromnegyed|negyed|fél)\\s*(\\d{1,2})(?:\\s*(?:-?kor|órakor|-?ra|-?re))?${E}`, 'iu');
// "9:30", "9.30-kor", "14:00-ra"
const COLON_CLOCK = new RegExp(`${B}(\\d{1,2})[:.](\\d{2})(?:\\s*(?:-?kor|órakor|-?ra|-?re))?${E}`, 'iu');
// "9-kor", "9 órakor", "14h", "9kor" - kotelezo jelzovel, kulonben egy puszta szam is ora lenne.
const SUFFIX_CLOCK = new RegExp(`${B}(\\d{1,2})\\s*(?:-?kor|órakor|óra|h|-?ra|-?re)${E}`, 'iu');
function take(text, re, hits) {
    const match = re.exec(text);
    if (!match)
        return null;
    const start = match.index;
    const end = start + match[0].length;
    // Mar felhasznalt reszbe nem nyulunk bele.
    if (hits.some((hit) => start < hit.end && end > hit.start))
        return null;
    hits.push({ start, end });
    return match;
}
function numberOf(word) {
    const lower = word.toLowerCase();
    if (/^\d+$/.test(lower))
        return Number(lower);
    if (lower === 'fél')
        return 0.5;
    if (lower === 'másfél')
        return 1.5;
    return NUMBER_WORDS[lower] ?? null;
}
function plusDays(wall, days) {
    const d = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days));
    return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
        hour: wall.hour,
        minute: wall.minute,
    };
}
function weekdayOf(wall) {
    return new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay();
}
function validDate(year, month, day) {
    if (month < 1 || month > 12 || day < 1)
        return false;
    return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** A szoveg kitakaritasa a kivett idopont-reszek utan. */
function cleanText(text, hits) {
    let rest = '';
    let at = 0;
    for (const hit of [...hits].sort((a, b) => a.start - b.start)) {
        rest += text.slice(at, hit.start) + ' ';
        at = hit.end;
    }
    rest += text.slice(at);
    return rest
        .replace(/\s+/g, ' ')
        .replace(/\s+([,.!?;:])/g, '$1')
        .trim()
        .replace(/^(?:[-–:,]\s*|hogy\s+)+/iu, '')
        .replace(/(?:\s*[-–:,])+$/u, '')
        .trim();
}
export function parseRemind(input, now, timeZone) {
    const text = input.trim();
    const hits = [];
    // ─── Relativ: "20 perc múlva" ─────────────────────────────────────────────
    const relative = take(text, RELATIVE, hits);
    if (relative) {
        const amount = numberOf(relative[1]);
        if (amount === null)
            return null;
        const unit = relative[2].toLowerCase();
        const minutes = unit.startsWith('perc')
            ? amount
            : unit.startsWith('ór')
                ? amount * 60
                : unit.startsWith('nap')
                    ? amount * 24 * 60
                    : amount * 7 * 24 * 60;
        const rest = cleanText(text, hits);
        if (!rest)
            return null;
        return { when: new Date(now.getTime() + Math.round(minutes) * 60_000), text: rest };
    }
    const today = wallClockIn(now, timeZone);
    // ─── Nap ───────────────────────────────────────────────────────────────────
    let day = null;
    let weekdayMode = null;
    const iso = take(text, ISO_DATE, hits);
    if (iso) {
        const [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
        if (!validDate(y, m, d))
            return null;
        day = { year: y, month: m, day: d, hour: 0, minute: 0 };
    }
    if (!day) {
        const monthDay = take(text, MONTH_DAY, hits);
        const numeric = monthDay ? null : take(text, NUMERIC_DATE, hits);
        const parts = monthDay
            ? {
                month: MONTHS.find(([re]) => re.test(monthDay[1].toLowerCase()))?.[1] ?? 0,
                day: Number(monthDay[2]),
            }
            : numeric
                ? { month: Number(numeric[1]), day: Number(numeric[2]) }
                : null;
        if (parts) {
            if (!validDate(today.year, parts.month, parts.day) &&
                !(parts.month === 2 && parts.day === 29)) {
                return null;
            }
            day = { year: today.year, month: parts.month, day: parts.day, hour: 0, minute: 0 };
            // Ami idén mar elmult, az jovore ertendo.
            const passed = parts.month < today.month || (parts.month === today.month && parts.day < today.day);
            if (passed || !validDate(day.year, day.month, day.day))
                day.year += 1;
            if (!validDate(day.year, day.month, day.day))
                return null;
        }
    }
    if (!day) {
        const word = take(text, DAY_WORD, hits);
        if (word) {
            const w = word[1].toLowerCase();
            day = plusDays(today, w === 'holnapután' ? 2 : w === 'holnap' ? 1 : 0);
        }
    }
    if (!day) {
        const weekday = take(text, WEEKDAY, hits);
        if (weekday) {
            const target = WEEKDAYS.find(([re]) => re.test(weekday[2].toLowerCase()))[1];
            weekdayMode = { target, next: Boolean(weekday[1]) };
        }
    }
    // ─── Ora ───────────────────────────────────────────────────────────────────
    let hour = null;
    let minute = 0;
    const daypart = take(text, DAYPART_RE, hits);
    const part = daypart ? DAYPART[daypart[1].toLowerCase()] : undefined;
    const fraction = take(text, FRACTION_CLOCK, hits);
    if (fraction) {
        const base = Number(fraction[2]);
        const kind = fraction[1].toLowerCase();
        hour = base - 1;
        minute = kind === 'fél' ? 30 : kind === 'negyed' ? 15 : 45;
        if (hour < 0)
            hour += 12;
    }
    else {
        const colon = take(text, COLON_CLOCK, hits);
        if (colon) {
            hour = Number(colon[1]);
            minute = Number(colon[2]);
        }
        else {
            const suffix = take(text, SUFFIX_CLOCK, hits);
            if (suffix)
                hour = Number(suffix[1]);
        }
    }
    if (hour !== null) {
        if (hour > 23 || minute > 59)
            return null;
        // "délután 3-kor" = 15:00. 12 felett mar egyertelmu.
        if (part?.pm && hour < 12)
            hour += 12;
    }
    else if (part) {
        hour = part.hour;
    }
    if (!day && !weekdayMode && hour === null)
        return null;
    // "ma" ora nelkul nem idopont.
    if (day && hour === null && day.day === today.day && day.month === today.month)
        return null;
    const h = hour ?? DEFAULT_HOUR;
    let wall;
    if (day) {
        wall = { ...day, hour: h, minute };
    }
    else if (weekdayMode) {
        const current = weekdayOf(today);
        let ahead = (weekdayMode.target - current + 7) % 7;
        if (weekdayMode.next) {
            // "jövő pénteken": a kovetkezo naptari het (hetfotol) adott napja.
            const toNextMonday = (8 - current) % 7 || 7;
            ahead = toNextMonday + ((weekdayMode.target + 6) % 7);
        }
        else if (ahead === 0) {
            // Ma van az a nap: ha az ora meg elottunk van, ma; kulonben egy het mulva.
            const candidate = fromWallClock({ ...today, hour: h, minute }, timeZone);
            if (candidate.getTime() <= now.getTime())
                ahead = 7;
        }
        wall = { ...plusDays(today, ahead), hour: h, minute };
    }
    else {
        // Csak ora: ma, ha meg elottunk van, kulonben holnap.
        wall = { ...today, hour: h, minute };
        if (fromWallClock(wall, timeZone).getTime() <= now.getTime()) {
            wall = { ...plusDays(today, 1), hour: h, minute };
        }
    }
    const rest = cleanText(text, hits);
    if (!rest)
        return null;
    return { when: fromWallClock(wall, timeZone), text: rest };
}
//# sourceMappingURL=remind-parse.js.map